import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { REQUEST_EMAIL_MUTATION, VERIFY_CODE_MUTATION } from './login.mutation';

export enum EmailSignInStatus {
  initial,
  codeSent,
  verifying,
}

export enum EmailSignInError {
  emailSendingFailure,
  otpVerificationFailure,
}

export const useSignInWithEmail = (): [
  () => void,
  (email: string, phone: string) => void,
  (code: string) => void,
  () => void,
  EmailSignInStatus,
  EmailSignInError | null,
  string | null,
] => {
  const [signInStatus, setSignInStatus] = useState(EmailSignInStatus.initial);
  const [loggedInUser, setLoggedInUser] = useState<string | null>(null);
  const [error, setError] = useState<EmailSignInError | null>(null);
  const [emailVerificationSid, setEmailVerificationSid] = useState<
    string | null
  >(null);
  const [email, setEmail] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);

  const [
    requestEmail,
    {
      data: requestEmailData,
      error: requestEmailError,
      loading: requestEmailLoading,
    },
  ] = useMutation(REQUEST_EMAIL_MUTATION, {
    onError: () => {
      setError(EmailSignInError.emailSendingFailure);
      setSignInStatus(EmailSignInStatus.initial);
    },
  });

  const [
    verifyOtpCode,
    {
      data: verifyCodeData,
      loading: verifyCodeLoading,
      error: verifyCodeError,
    },
  ] = useMutation(VERIFY_CODE_MUTATION, {
    onError: () => {
      setError(EmailSignInError.otpVerificationFailure);
      // Keep the user on the code entry screen so they can retry.
      setSignInStatus(EmailSignInStatus.codeSent);
    },
  });

  const initialize = useCallback(() => {}, []);

  const signInWithEmail = useCallback(
    (email: string, phone: string) => {
      setError(null);
      setEmail(email);
      setPhone(phone);
      requestEmail({
        variables: {
          email,
          phone,
        },
      }).catch(() => {
        // Errors are surfaced via the onError handler above.
      });
      setSignInStatus(EmailSignInStatus.codeSent);
    },
    [requestEmail],
  );

  useEffect(() => {
    if (requestEmailLoading || requestEmailError || !requestEmailData) {
      return;
    }
    if (requestEmailData) {
      console.log('requestEmailData', requestEmailData);
      setEmailVerificationSid(requestEmailData.requestEmail);
    }
  }, [requestEmailData, requestEmailError, requestEmailLoading]);

  const verifyCode = useCallback(
    (code: string) => {
      setError(null);
      setSignInStatus(EmailSignInStatus.verifying);
      verifyOtpCode({
        variables: {
          phone: phone,
          email: email,
          verificationSid: emailVerificationSid,
          code,
        },
      }).catch(() => {
        // Errors are surfaced via the onError handler above.
      });
    },
    [email, emailVerificationSid, phone, verifyOtpCode],
  );

  useEffect(() => {
    if (verifyCodeLoading || verifyCodeError || !verifyCodeData) {
      return;
    }
    console.log('verifyCodeData', verifyCodeData);
    setLoggedInUser(verifyCodeData.verifyCode);
  }, [verifyCodeData, verifyCodeError, verifyCodeLoading]);

  const reset = useCallback(() => {
    setSignInStatus(EmailSignInStatus.initial);
    setError(null);
    initialize();
  }, [initialize]);

  return [
    initialize,
    signInWithEmail,
    verifyCode,
    reset,
    signInStatus,
    error,
    loggedInUser,
  ];
};
